import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { forwardRef } from 'preact/compat';
import { BRAND_CLASSES } from '@/constants/theme';
/**
 * Select/Dropdown component with React Hook Form integration
 *
 * @example
 * // Basic usage with React Hook Form
 * const { register, formState: { errors } } = useForm()
 *
 * <Select
 *   label="Type"
 *   required
 *   error={errors.type?.message}
 *   options={[
 *     { value: '1', label: 'Option 1' },
 *     { value: '2', label: 'Option 2' },
 *   ]}
 *   placeholder="Select a type"
 *   {...register('type', { required: 'Type is required' })}
 * />
 *
 * @example
 * // With options from lookup
 * const webTypes = getWebTypes()
 * const options = webTypes.map(t => ({ value: t.Key, label: t.Description }))
 *
 * <Select
 *   label="Web Type"
 *   required
 *   options={options}
 *   {...register('type', { required: 'Required' })}
 * />
 */
export const Select = forwardRef(({ label, required = false, name, error, helperText, options, placeholder = 'Select an option', className = '', containerClassName = '', labelSize = 'sm', labelClassName = '', onChange, onBlur, defaultValue = '', ...props }, ref) => {
    const hasError = !!error;
    const selectClasses = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${hasError
        ? `${BRAND_CLASSES.INPUT.ERROR}`
        : `${BRAND_CLASSES.INPUT.NORMAL} ${BRAND_CLASSES.FOCUS.PRIMARY}`} ${className}`;
    const labelSizeClasses = labelSize === 'md' ? 'text-sm' : 'text-sm';
    return (_jsxs("div", { className: containerClassName, children: [label && (_jsxs("label", { htmlFor: name, className: `block ${labelSizeClasses} font-medium ${BRAND_CLASSES.INPUT.LABEL} mb-2 ${labelClassName}`, children: [label, required && _jsx("span", { className: `${BRAND_CLASSES.INPUT.REQUIRED} ml-1`, children: "*" })] })), _jsxs("select", { id: name, name: name, onChange: onChange, onBlur: onBlur, className: selectClasses, defaultValue: defaultValue, ref: ref, ...props, children: [_jsx("option", { value: "", disabled: true, children: placeholder }), options.map((option) => (_jsx("option", { value: option.value, disabled: option.disabled, children: option.label }, option.value)))] }), error && (_jsx("p", { className: `mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`, children: error })), helperText && !error && (_jsx("p", { className: `mt-1 text-xs ${BRAND_CLASSES.INPUT.HELPER}`, children: helperText }))] }));
});
Select.displayName = 'Select';
